import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { selectPrimaryRole } from "../../lib/auth-role";
import { requireSupabaseClient, supabaseClient } from "../../lib/supabase";
import { userRoles, type SessionUser, type UserRole } from "../../types/domain";
import {
  SessionIdleGuard,
  clearSessionActivity,
  markSessionActivity,
  readSessionActivity,
  sessionIsExpired,
} from "./SessionIdleGuard";

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  mode: "supabase";
  sessionError: string;
  recoveryMode: boolean;
  login: (email: string, password: string) => Promise<SessionUser>;
  register: (name: string, email: string, password: string) => Promise<void>;
  recover: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function sameSessionUser(current: SessionUser | null, next: SessionUser) {
  return Boolean(
    current &&
      current.id === next.id &&
      current.email === next.email &&
      current.name === next.name &&
      current.role === next.role &&
      current.municipalityId === next.municipalityId &&
      current.source === next.source &&
      current.roles.length === next.roles.length &&
      current.roles.every((role, index) => role === next.roles[index]),
  );
}

function roleFromRelation(value: unknown): UserRole | null {
  const relation = Array.isArray(value) ? value[0] : value;
  if (!relation || typeof relation !== "object") return null;
  const code = (relation as { code?: unknown }).code;
  return typeof code === "string" && userRoles.includes(code as UserRole)
    ? (code as UserRole)
    : null;
}

async function resolveSupabaseSession(session: Session): Promise<SessionUser> {
  const client = requireSupabaseClient();
  const { data: profile, error: profileError } = await client
    .from("profiles")
    .select("id, municipality_id, full_name")
    .eq("id", session.user.id)
    .maybeSingle();
  if (profileError) throw new Error("No se pudo verificar el perfil.");
  if (!profile) {
    throw new Error("La cuenta todavía no tiene un perfil municipal habilitado.");
  }

  const { data: assignments, error: roleError } = await client
    .from("user_roles")
    .select("roles!inner(code)")
    .eq("user_id", session.user.id)
    .eq("municipality_id", profile.municipality_id)
    .is("revoked_at", null);
  if (roleError) throw new Error("No se pudieron verificar los permisos.");

  const roles = (assignments ?? [])
    .map((assignment) => roleFromRelation((assignment as { roles?: unknown }).roles))
    .filter((role): role is UserRole => Boolean(role));
  const uniqueRoles = [...new Set(roles)];
  const primaryRole = selectPrimaryRole(uniqueRoles);
  if (!primaryRole) {
    throw new Error("La cuenta no posee un rol municipal vigente.");
  }

  return {
    id: session.user.id,
    email: session.user.email ?? "",
    name: String(profile.full_name || "Usuario"),
    role: primaryRole,
    roles: uniqueRoles,
    municipalityId: String(profile.municipality_id),
    source: "supabase",
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState("");
  const [recoveryMode, setRecoveryMode] = useState(false);
  const synchronizationId = useRef(0);
  const currentUserId = useRef<string | null>(user?.id ?? null);
  const mode = "supabase" as const;

  const clearLocalState = useCallback(() => {
    if (currentUserId.current) clearSessionActivity(currentUserId.current);
    currentUserId.current = null;
    queryClient.clear();
    setUser(null);
  }, [queryClient]);

  const applyUser = useCallback(
    (sessionUser: SessionUser) => {
      if (currentUserId.current && currentUserId.current !== sessionUser.id) {
        clearSessionActivity(currentUserId.current);
        queryClient.clear();
      }
      currentUserId.current = sessionUser.id;
      // Supabase puede volver a emitir SIGNED_IN/TOKEN_REFRESHED al recuperar el
      // foco de una pestaña. Conservamos la misma referencia cuando el perfil no
      // cambió para que los editores abiertos no se reinicialicen.
      setUser((current) => (sameSessionUser(current, sessionUser) ? current : sessionUser));
      setSessionError("");
    },
    [queryClient],
  );

  const synchronizeSupabaseSession = useCallback(
    async (session: Session | null) => {
      const requestId = ++synchronizationId.current;
      if (!session) {
        clearLocalState();
        setLoading(false);
        return;
      }

      const existingActivity = readSessionActivity(session.user.id);
      if (existingActivity !== null && sessionIsExpired(session.user.id)) {
        synchronizationId.current += 1;
        await supabaseClient?.auth.signOut({ scope: "local" });
        clearLocalState();
        setSessionError("La sesión se cerró después de 5 minutos de inactividad.");
        setLoading(false);
        return;
      }
      if (existingActivity === null) markSessionActivity(session.user.id);

      // Las renovaciones del token no deben desmontar la ruta ni borrar formularios.
      const requiresBlockingVerification = currentUserId.current !== session.user.id;
      if (requiresBlockingVerification) setLoading(true);
      try {
        const sessionUser = await resolveSupabaseSession(session);
        if (requestId !== synchronizationId.current) return;
        applyUser(sessionUser);
      } catch (reason) {
        if (requestId !== synchronizationId.current) return;
        clearLocalState();
        setSessionError(
          reason instanceof Error
            ? reason.message
            : "No se pudo verificar la sesión municipal.",
        );
      } finally {
        if (requestId === synchronizationId.current && requiresBlockingVerification) {
          setLoading(false);
        }
      }
    },
    [applyUser, clearLocalState],
  );

  useEffect(() => {
    if (!supabaseClient) {
      setSessionError("El servicio de acceso no está disponible en este momento.");
      setLoading(false);
      return;
    }

    let active = true;
    void supabaseClient.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setSessionError("No se pudo recuperar la sesión.");
        setLoading(false);
        return;
      }
      void synchronizeSupabaseSession(data.session);
    });

    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      setRecoveryMode(event === "PASSWORD_RECOVERY");
      window.setTimeout(() => {
        if (active) void synchronizeSupabaseSession(session);
      }, 0);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [synchronizeSupabaseSession]);

  const login = useCallback(
    async (email: string, password: string) => {
      setSessionError("");
      const client = requireSupabaseClient();
      setLoading(true);
      const { data, error } = await client.auth.signInWithPassword({
        email,
        password,
      });
      if (error || !data.session) {
        setLoading(false);
        throw new Error("Correo o contraseña incorrectos.");
      }

      const signedInUserId = data.session.user.id;
      // SIGNED_IN también dispara la sincronización global. Renovamos la marca
      // antes de consultar perfil y roles para que una actividad antigua del
      // mismo navegador no cierre por error la sesión que acaba de autenticarse.
      markSessionActivity(signedInUserId);

      try {
        const sessionUser = await resolveSupabaseSession(data.session);
        applyUser(sessionUser);
        return sessionUser;
      } catch (reason) {
        clearSessionActivity(signedInUserId);
        await client.auth.signOut({ scope: "local" });
        clearLocalState();
        throw reason;
      } finally {
        setLoading(false);
      }
    },
    [applyUser, clearLocalState],
  );

  const register = useCallback(
    async (name: string, email: string, password: string) => {
      const client = requireSupabaseClient();
      const { error } = await client.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: name },
          emailRedirectTo: `${window.location.origin}/ingresar`,
        },
      });
      if (error) throw new Error("No se pudo completar el registro.");
    },
    [],
  );

  const recover = useCallback(async (email: string) => {
    const client = requireSupabaseClient();
    const { error } = await client.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/ingresar`,
    });
    if (error) throw new Error("No se pudo iniciar la recuperación.");
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    if (password.length < 10) {
      throw new Error("Usá una contraseña de al menos 10 caracteres.");
    }
    const client = requireSupabaseClient();
    const { error } = await client.auth.updateUser({ password });
    if (error) throw new Error("No se pudo actualizar la contraseña.");
    setRecoveryMode(false);
  }, []);

  const logout = useCallback(async () => {
    synchronizationId.current += 1;
    if (supabaseClient) {
      await supabaseClient.auth.signOut({ scope: "local" });
    }
    clearLocalState();
    setRecoveryMode(false);
    setLoading(false);
  }, [clearLocalState]);

  const expireSession = useCallback(async () => {
    synchronizationId.current += 1;
    if (supabaseClient) {
      await supabaseClient.auth.signOut({ scope: "local" });
    }
    clearLocalState();
    setRecoveryMode(false);
    setLoading(false);
    setSessionError("La sesión se cerró después de 5 minutos de inactividad.");
  }, [clearLocalState]);

  const value = useMemo(
    () => ({
      user,
      loading,
      mode,
      sessionError,
      recoveryMode,
      login,
      register,
      recover,
      updatePassword,
      logout,
    }),
    [
      user,
      loading,
      mode,
      sessionError,
      recoveryMode,
      login,
      register,
      recover,
      updatePassword,
      logout,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {user ? <SessionIdleGuard userId={user.id} onExpire={expireSession} /> : null}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth debe usarse dentro de AuthProvider.");
  return value;
}

